import React, { FC, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import {
  makeStyles,
  Theme,
  createStyles,
  Grid,
  Divider,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { BottomSheet } from 'react-spring-bottom-sheet';
import Button from '@material-ui/core/Button';

import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import {
  OutageLookupParams,
  OutageLookupResponse,
  OutageLookUpType,
  PremiseInfo,
  ServicePointDetail,
} from '../../../../__generated__/pge-types';
import ReportOutageSymbol from '../../../../static/reportOutageSymbol.svg';
import BottomDrawerLayout, {
  DrawerSnapType,
} from '../bottom-drawer-layout/BottomDrawerLayout';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: {
      marginTop: '1rem',
      marginBottom: '1rem',
    },
    formItem: {
      marginTop: '1rem',
      paddingLeft: 0,
      overflow: 'auto',
    },
    bodyText: {
      lineHeight: '18px',
      fontSize: 16,
    },
    radioButtonIcon: {
      '& .MuiSvgIcon-root': {
        fontSize: '1rem',
      },
    },
    submitButtonWrapper: {
      margin: theme.spacing(2, 0, 1, 0),
      textAlign: 'center',
      float: 'none',
    },
    submitButton: {
      padding: theme.spacing(1.625),
      backgroundColor: colors.orange,
      borderRadius: 50,
      color: colors.white,
      fontSize: '1.125rem',
      lineHeight: '1.125rem',
      width: '100%',
      '&:active': {
        backgroundColor: colors.orange,
      },
      '&:hover': {
        backgroundColor: colors.orange,
      },
      '&.MuiButton-root.Mui-disabled': {
        color: colors.quickSilverGrey,
        backgroundColor: colors.disableColor,
      },
    },
    bottomSheet: {
      padding: 20,
    },
    heading: {
      fontSize: 20,
      lineHeight: '26px',
      fontWeight: 'bold',
    },
    backButton: {
      fontSize: '1.125rem',
      lineHeight: '1.375rem',
      padding: theme.spacing(1.125, 1.875),
      float: 'left',
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'underline',
      },
      [theme.breakpoints.down('xs')]: {
        float: 'none',
        textAlign: 'center',
      },
    },
    titleSection: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: 8,
      marginTop: -20,
    },
    outageTitle: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: '1.625rem',
      color: colors.noirBlurLight,
      paddingLeft: 10,
    },
    selectAddressTitleWrap: {
      position: 'relative',
    },
    closeBtn: {
      position: 'absolute',
      top: '-10px',
      right: '-10px',
    },
    cancelButton: {
      color: colors.brightBlue,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

type Props = {
  open: boolean;
  onClose: () => void;
  outageDetailData: OutageLookupResponse;
  selectedPremiseParams: (
    data: OutageLookupParams,
    houseNumber: string | undefined,
    premise: PremiseInfo | null,
  ) => void;
  reportOutageFromIcon: boolean;
  outageFindAccount?: boolean;
};

const MultiplePremiseMobileView: FC<Props> = ({
  open,
  onClose,
  outageDetailData,
  selectedPremiseParams,
  reportOutageFromIcon,
  outageFindAccount,
}: Props) => {
  const classes = useStyles();
  const [premiseChecked, setPremiseChecked] = useState<boolean>(false);
  const [premiseHouseNumber, setPremiseHouseNumber] = useState<
    string | undefined
  >();
  const [premiseEncryptedValue, setPremiseEncryptedValue] = useState<string>(
    '',
  );
  const [selectedPremise, setSelectedPremise] = useState<PremiseInfo | null>(
    null,
  );

  const { t } = useTranslation();

  const isMultipleAddressExist =
    outageDetailData?.multiplePremiseList !== null &&
    outageDetailData?.multiplePremiseList !== undefined &&
    outageDetailData?.multiplePremiseList?.length > 1
      ? true
      : false;

  const onHouseNumberChecked = (e: any) => {
    const selectedVal = JSON.parse(e.target.value);
    setPremiseEncryptedValue(selectedVal.encryptedValue);
    setPremiseChecked(true);
    setPremiseHouseNumber(selectedVal.houseNumber);
    setSelectedPremise(selectedVal.premise);
  };

  const handleOnSubmit = () => {
    if (premiseChecked && premiseEncryptedValue) {
      const params: OutageLookupParams = {
        lookupType: OutageLookUpType.Premise,
        encryptedValue: premiseEncryptedValue,
      };
      selectedPremiseParams(params, premiseHouseNumber, selectedPremise);
    }
  };

  return (
    <BottomDrawerLayout
      onClose={onClose}
      data-testid={'notification-options'}
      refId={7}
      defaultSnap={DrawerSnapType.default}
    >
      <Box component="div" className={classes.bottomSheet}>
        <Grid item>
          {reportOutageFromIcon && !outageFindAccount ? (
            <>
              <Grid className={classes.titleSection}>
                <ReportOutageSymbol />
                <Typography variant={'h2'} className={classes.outageTitle}>
                  {t('REPORT_OUTAGE_TITLE')}
                </Typography>
              </Grid>
            </>
          ) : (
            <Grid className={classes.selectAddressTitleWrap}>
              <Typography variant={'h2'} noWrap className={classes.heading}>
                {t('SELECT_SERVICE_ADDRESS')}
              </Typography>
              <Box className={classes.closeBtn}>
                <IconButton onClick={() => onClose()}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>
        <Divider className={classes.divider} />
        <Grid item>
          <Typography variant={'body1'} className={classes.bodyText}>
            {t('MULTIPLE_SERVICE_ADDRESS_TEXT')}
          </Typography>
        </Grid>
        <Grid item className={classes.formItem}>
          {isMultipleAddressExist && (
            <Box flexDirection={'row'}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label={'report-outage-multiple-addresses'}
                  name={'selectAddressForOutage'}
                  onChange={e => onHouseNumberChecked(e)}
                >
                  <Grid
                    container
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingLeft: 20,
                    }}
                    direction={'column'}
                  >
                    {outageDetailData?.multiplePremiseList?.map(
                      (account, idx: number) => {
                        // @ts-ignore
                        const premiseInfo = account?.premiseInfo;
                        return (
                          <FormControlLabel
                            value={JSON.stringify({
                              encryptedValue: account?.encryptedValue,
                              houseNumber: account?.houseNumber,
                              premise: premiseInfo,
                            })}
                            control={
                              <Radio
                                color={'primary'}
                                className={classes.radioButtonIcon}
                              />
                            }
                            label={
                              <Box
                                css={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography component="span">
                                  {account?.houseNumber +
                                    ' ' +
                                    account?.last4CharAddress}
                                </Typography>
                                {premiseInfo?.servicePointDetails?.find(
                                  (servicePoint: ServicePointDetail) =>
                                    servicePoint?.outageInfo?.hasKnownOutage,
                                ) && (
                                  <Typography
                                    component="span"
                                    css={{
                                      color: colors.errorRed,
                                      fontSize: '0.8rem',
                                      lineHeight: '0.8rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    (Outage Reported)
                                  </Typography>
                                )}
                              </Box>
                            }
                            data-testid={`house-number-item-${idx}`}
                            key={`house-number-item-${idx}`}
                          />
                        );
                      },
                    )}
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </Grid>

        <Grid xs={12} sm={6} className={classes.submitButtonWrapper}>
          <Button
            disabled={premiseChecked && premiseEncryptedValue ? false : true}
            size={'large'}
            className={classes.submitButton}
            onClick={async () => {
              await handleOnSubmit();
            }}
          >
            <Typography variant={'h4'} noWrap>
              {t('FIND_ADDRESS')}
            </Typography>
          </Button>
          <Box className={classes.backButton}>
            <Button
              className={classes.cancelButton}
              onClick={() => onClose()}
              disableRipple={true}
            >
              <Typography variant={'h4'} noWrap>
                {t('CANCEL')}
              </Typography>
            </Button>
          </Box>
        </Grid>
      </Box>
    </BottomDrawerLayout>
  );
};

export default MultiplePremiseMobileView;
